import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { language, setLanguage, t } = useLanguage();

  const navItems = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.services'), path: '/services' },
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const renderLangToggle = (onDark) => (
    <ButtonGroup variant="outlined" size="small" aria-label={t('nav.toggleAria')}>
      <Button
        onClick={() => setLanguage('en')}
        aria-pressed={language === 'en'}
        sx={{
          fontWeight: language === 'en' ? 700 : 400,
          backgroundColor: language === 'en' ? 'secondary.main' : 'transparent',
          color: language === 'en' ? 'secondary.contrastText' : (onDark ? 'inherit' : 'primary.main'),
          borderColor: onDark ? 'rgba(255,255,255,0.6)' : 'primary.main',
        }}
      >
        EN
      </Button>
      <Button
        onClick={() => setLanguage('es')}
        aria-pressed={language === 'es'}
        sx={{
          fontWeight: language === 'es' ? 700 : 400,
          backgroundColor: language === 'es' ? 'secondary.main' : 'transparent',
          color: language === 'es' ? 'secondary.contrastText' : (onDark ? 'inherit' : 'primary.main'),
          borderColor: onDark ? 'rgba(255,255,255,0.6)' : 'primary.main',
        }}
      >
        ES
      </Button>
    </ButtonGroup>
  );

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleDrawerToggle} aria-label="close menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.path}
            button
            onClick={() => handleNavigation(item.path)}
            sx={{
              backgroundColor: location.pathname === item.path ? 'primary.light' : 'transparent',
              color: location.pathname === item.path ? 'white' : 'inherit',
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem sx={{ justifyContent: 'center', mt: 1 }}>
          {renderLangToggle(false)}
        </ListItem>
        <ListItem button onClick={() => handleNavigation('/contact')}>
          <Button variant="contained" color="secondary" fullWidth sx={{ mt: 2 }}>
            {t('nav.requestQuote')}
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" sx={{ backgroundColor: 'primary.main' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: '#fdfaf1',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                mr: 1,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Box
                component="img"
                src="/images/chato_hs_logo.png"
                alt="Chato Home Services Logo"
                sx={{ height: { xs: 36, sm: 44 }, width: 'auto', display: 'block' }}
              />
            </Box>
          </Box>

          {isMobile ? (
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    textDecoration: location.pathname === item.path ? 'underline' : 'none',
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {renderLangToggle(true)}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/contact')}
                sx={{ ml: 1 }}
              >
                {t('nav.requestQuote')}
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
